import {Observable} from 'rxjs';
import {CpmCLocationApiModel} from '../../models/c-customer/cpmc-location.api-model';
import {
    CpmCLocationCreateApiSchema,
    CpmCLocationDeletePayloadApiSchema,
    CpmCLocationUpdateApiSchema
} from '../../schemas/c-customer/cpmc-location.api-schemas';
import {CpmCApiBridge} from '../../../../../src/app/cpmc-api-bridge';
import {CpmCQueryPaginatorConfig, CpmCQueryPaginatorConfigUtils} from '../../cpmc-defines';
import {ECpmCLocationTypeApiEnum} from '../../enums/c-customer/cpmc-location.api-enums';
import {AllowedQueryParamTypes, QueryParams} from '../../../../../sdk/src/utils/query.utils';
import {CpmCLocationTreeApiView} from '../../views/c-customer/cpmc-location-tree.api-view';
import {CpmCPaginatedResultAPISchema} from '../../schemas/cpmc-paginated-result.api-schemas';

export type CpmCGetLocationsParams = {
    [K in keyof CpmCLocationApiModel]?: CpmCLocationApiModel[K] extends AllowedQueryParamTypes
        ? CpmCLocationApiModel[K]
        : never;
} & {downToType?: ECpmCLocationTypeApiEnum} & QueryParams;

export type CpmCGetLocationTreeParams = {
    [K in keyof CpmCLocationApiModel]?: CpmCLocationApiModel[K] extends AllowedQueryParamTypes
        ? CpmCLocationApiModel[K]
        : never;
} & {downToType?: ECpmCLocationTypeApiEnum} & QueryParams;

/**
 * Location Api Service
 */
export class CpmCLocationApiService {
    public static readonly dPaginatorConfig: CpmCQueryPaginatorConfig = {limit: -1, sort: 'name', order: 1};

    // ===============================================================
    // ===============================================================

    public static baseUrl(inCustomerUUID: string | undefined, inOrganizationUUID: string | undefined) {
        const base = CpmCApiBridge.getBackendBaseURL() + 'api/core/v1/';
        const customerUUID = CpmCApiBridge.getCurrentCustomerUUID();
        const organizationUUID = CpmCApiBridge.getCurrentOrganizationUUID();

        return (
            base +
            `customer/${customerUUID || inCustomerUUID}/organization/${organizationUUID || inOrganizationUUID}/location`
        );
    }

    // ===============================================================
    // ===============================================================

    public static endpointList(inCustomerUUID: string | undefined, inOrganizationUUID: string | undefined): string {
        return `${CpmCLocationApiService.baseUrl(inCustomerUUID, inOrganizationUUID)}/list`;
    }
    public static endpointTree(inCustomerUUID: string | undefined, inOrganizationUUID: string | undefined): string {
        return `${CpmCLocationApiService.baseUrl(inCustomerUUID, inOrganizationUUID)}/tree`;
    }
    public static endpointNew(inCustomerUUID: string | undefined, inOrganizationUUID: string | undefined): string {
        return `${CpmCLocationApiService.baseUrl(inCustomerUUID, inOrganizationUUID)}`;
    }
    public static endpointGet(
        inCustomerUUID: string | undefined,
        inOrganizationUUID: string | undefined,
        locationUUID: string
    ): string {
        return `${CpmCLocationApiService.baseUrl(inCustomerUUID, inOrganizationUUID)}/${locationUUID}`;
    }
    public static endpointEdit(
        inCustomerUUID: string | undefined,
        inOrganizationUUID: string | undefined,
        locationUUID: string
    ): string {
        return `${CpmCLocationApiService.baseUrl(inCustomerUUID, inOrganizationUUID)}/${locationUUID}`;
    }
    public static endpointDelete(
        inCustomerUUID: string | undefined,
        inOrganizationUUID: string | undefined,
        locationUUID: string
    ): string {
        return `${CpmCLocationApiService.baseUrl(inCustomerUUID, inOrganizationUUID)}/${locationUUID}`;
    }

    // ===============================================================
    // ===============================================================

    public static getLocationList(
        params: CpmCGetLocationsParams = {},
        paginatorConfig: CpmCQueryPaginatorConfig = CpmCLocationApiService.dPaginatorConfig
    ): Observable<CpmCPaginatedResultAPISchema<CpmCLocationApiModel>> {
        return CpmCApiBridge.httpGet<CpmCPaginatedResultAPISchema<CpmCLocationApiModel>>(
            CpmCLocationApiService.endpointList(undefined, undefined),
            {
                ...(params as QueryParams),
                ...CpmCQueryPaginatorConfigUtils.toQueryParams(paginatorConfig)
            }
        );
    }
    public static getLocationTree(
        params: CpmCGetLocationTreeParams = {},
        paginatorConfig: CpmCQueryPaginatorConfig = CpmCLocationApiService.dPaginatorConfig,
        inOrganizationUUID: string | undefined = undefined
    ): Observable<CpmCPaginatedResultAPISchema<CpmCLocationTreeApiView>> {
        return CpmCApiBridge.httpGet<CpmCPaginatedResultAPISchema<CpmCLocationTreeApiView>>(
            CpmCLocationApiService.endpointTree(undefined, inOrganizationUUID),
            {
                ...(params as QueryParams),
                ...CpmCQueryPaginatorConfigUtils.toQueryParams(paginatorConfig)
            }
        );
    }
    public static getLocationByUUID(
        locationUUID: string,
        inOrganizationUUID: string | undefined = undefined
    ): Observable<CpmCLocationApiModel> {
        return CpmCApiBridge.httpGet<CpmCLocationApiModel>(
            CpmCLocationApiService.endpointGet(undefined, inOrganizationUUID, locationUUID)
        );
    }
    public static createLocation(payload: CpmCLocationCreateApiSchema): Observable<CpmCLocationApiModel> {
        return CpmCApiBridge.httpPost<CpmCLocationApiModel>(
            CpmCLocationApiService.endpointNew(undefined, undefined),
            payload
        );
    }
    public static updateLocation(
        locationUUID: string,
        payload: CpmCLocationUpdateApiSchema
    ): Observable<CpmCLocationApiModel> {
        return CpmCApiBridge.httpPut<CpmCLocationApiModel>(
            CpmCLocationApiService.endpointEdit(undefined, undefined, locationUUID),
            payload
        );
    }
    public static deleteLocation(
        locationUUID: string,
        payload?: CpmCLocationDeletePayloadApiSchema
    ): Observable<boolean> {
        return CpmCApiBridge.httpDelete<boolean>(
            CpmCLocationApiService.endpointDelete(undefined, undefined, locationUUID),
            undefined,
            payload
        );
    }
}
